export interface Link {
  slug: string;
  destination: string;
  description?: string | null;
  createdAt: string;
  updatedAt: string;
  shortUrl?: string;
  clicks: number;
  folderId?: number | null;
  position?: number;
}

export interface Folder {
  id: number;
  name: string;
  createdAt: string;
  position: number;
}

export interface LinkPayload {
  slug?: string;
  destination: string;
  description?: string | null;
  password?: string;
  folderId?: number | null;
}

export type Plan = 'free' | 'pro';

export interface User {
  id: string;
  email: string;
  createdAt: string;
  plan: Plan;
  linksUsed?: number;
  linksLimit?: number | null;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface AuthCredentials {
  email: string;
  password: string;
}

interface ApiClientConfig {
  baseUrl: string;
  token?: string;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  data?: unknown;

  constructor(message: string, status: number, code?: string, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

const normalizeBase = (base: string) => {
  if (!base) return '/api';
  return base.endsWith('/') ? base.slice(0, -1) : base;
};

export const createApiClient = ({ baseUrl, token }: ApiClientConfig) => {
  const base = normalizeBase(baseUrl);

  const request = async <T>(path: string, init: RequestInit = {}, search?: Record<string, string | undefined>) => {
    let url = `${base}${path}`;
    if (search) {
      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const headers = new Headers(init.headers || {});
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json');
    }

    const response = await fetch(url, {
      ...init,
      headers,
    });

    const text = await response.text();

    if (!response.ok) {
      let message = 'Request failed';
      let code: string | undefined;
      let data: unknown = undefined;
      if (text) {
        try {
          const parsed = JSON.parse(text);
          if (parsed && typeof parsed === 'object' && 'error' in parsed) {
            message = (parsed as { error: string }).error as string;
            if ('code' in parsed && typeof (parsed as { code?: unknown }).code === 'string') {
              code = (parsed as { code: string }).code;
            }
            data = parsed;
          } else {
            message = text;
          }
        } catch {
          message = text;
        }
      }
      throw new ApiError(message, response.status, code, data);
    }

    if (!text) {
      return {} as T;
    }

    try {
      return JSON.parse(text) as T;
    } catch {
      throw new Error('Réponse invalide du serveur');
    }
  };

  return {
    register: async (credentials: AuthCredentials): Promise<AuthResponse> => {
      return request<AuthResponse>('/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
    },
    login: async (credentials: AuthCredentials): Promise<AuthResponse> => {
      return request<AuthResponse>('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
    },
    me: async (): Promise<User> => {
      const data = await request<{ user: User }>('/auth/me', { method: 'GET' });
      return data.user;
    },
    googleAuthUrl: async (redirect: string): Promise<{ url: string }> => {
      return request<{ url: string }>(
        '/auth/oauth/google',
        { method: 'GET' },
        { redirect }
      );
    },
    createCheckout: async (): Promise<{ url: string; alreadyPro?: boolean }> => {
      return request<{ url: string; alreadyPro?: boolean }>('/billing/checkout', {
        method: 'POST',
      });
    },
    list: async (searchValue?: string): Promise<Link[]> => {
      const data = await request<{ links: Link[] }>(
        '/links',
        { method: 'GET', headers: { Accept: 'application/json' } },
        searchValue ? { search: searchValue } : undefined
      );
      return data.links || [];
    },
    create: async (payload: LinkPayload): Promise<Link> => {
      const data = await request<{ link: Link }>(
        '/links',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      return data.link;
    },
    update: async (slug: string, payload: LinkPayload): Promise<Link> => {
      const data = await request<{ link: Link }>(
        `/links/${encodeURIComponent(slug)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      return data.link;
    },
    remove: async (slug: string): Promise<void> => {
      await request(`/links/${encodeURIComponent(slug)}`, {
        method: 'DELETE',
      });
    },
    health: async (): Promise<boolean> => {
      try {
        await request('/health');
        return true;
      } catch (error) {
        return false;
      }
    },
    getAnalytics: async (slug: string): Promise<AnalyticsEvent[]> => {
      const data = await request<{ analytics: AnalyticsEvent[] }>(
        `/links/${encodeURIComponent(slug)}/analytics`,
        { method: 'GET' }
      );
      return data.analytics;
    },

    // Folders API
    getFolders: async (): Promise<Folder[]> => {
      const data = await request<{ folders: Folder[] }>('/folders', { method: 'GET' });
      return data.folders;
    },

    createFolder: async (name: string): Promise<Folder> => {
      const data = await request<{ folder: Folder }>('/folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      return data.folder;
    },

    updateFolder: async (id: number, name: string): Promise<Folder> => {
      return await request(`/folders/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
    },

    deleteFolder: async (id: number): Promise<void> => {
      await request(`/folders/${id}`, { method: 'DELETE' });
    },

    reorderFolders: async (order: number[]): Promise<void> => {
      await request('/folders/reorder', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order }),
      });
    },

    reorderLinks: async (slugs: string[]): Promise<void> => {
      await request('/links/reorder', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slugs }),
      });
    },

    // Fractional indexing position updates
    updateLinkPosition: async (slug: string, position: { beforeSlug?: string | null; afterSlug?: string | null }): Promise<{ positionKey: string }> => {
      return request<{ success: boolean; positionKey: string }>(`/links/${encodeURIComponent(slug)}/position`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(position),
      });
    },

    updateFolderPosition: async (id: number, position: { beforeId?: number | null; afterId?: number | null }): Promise<{ positionKey: string }> => {
      return request<{ success: boolean; positionKey: string }>(`/folders/${id}/position`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(position),
      });
    },
  };
};

export interface AnalyticsEvent {
  timestamp: string;
  referer: string | null;
  browser: string;
  device: string;
  country: string;
}
